import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { S3, WIDGET_ICONS } from "../../../../../../constants";
import useCompareState from "../../../../../../hooks/useCompareState";
import FileUpload from "../../../../../../parts/inputs/file-upload";
import ImageRadio from "../../../../../../parts/inputs/image-radio";
import ImageWidgetUploadInput from "../../../../../../parts/inputs/image-widget-upload";
import ConfirmationModal from "../../../../../../parts/modals/ConfirmationModal";
import { EditWidgetInfoModal } from "../../../../../../parts/modals/EditWidgetInfo";
import SvgIcon from "../../../../../../parts/SvgIcon";
import scriptLoader from "../../../../../../utils/scriptLoader";
import { isPullActiveCoach } from "../../../../../../utils/user-helpers";
import {
  deleteWidgetUtil,
  orderWidgets,
} from "../../../../../../utils/widgets";
import { getUserToRequest } from "../../../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { EditFile } from "./EditFile";
import WidgetManagerHeader from "./WidgetManagerHeader";

/**
 * Publish a playlist
 * @param {*} e
 * @param {*} id
 */
const publishWidget = (e, id) => {
  e.stopPropagation();
  e.preventDefault();
  submit(`/actions/files/status/${id}`, {
    method: "POST",
    body: { status: "published" },
  });
};

/**
 * Unpublish a playlist
 * @param {*} e
 * @param {*} id
 */
const unpublishWidget = (e, id) => {
  e.stopPropagation();
  e.preventDefault();
  submit(`/actions/files/status/${id}`, {
    method: "POST",
    body: { status: "draft" },
  });
};

/**
 * Change the status of a playlist
 * @param {*} param0
 * @returns
 */
const WidgetToggle = ({ status, id }) => {
  const { t } = useTranslation("coaching-portal");
  const [value, setValue] = useState(status === "draft" ? false : true);
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);

  const handleChange = (e) => {
    !value ? unpublishWidget(e, id) : publishWidget(e, id);
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 500);
  };
  return (
    <>
      <input
        type="checkbox"
        id={`switch-${id}`}
        name={`switch-${id}`}
        role="switch"
        onChange={() => {
          setValue(!value);
          setOpenedConfirmationModal(!openedConfirmationModal);
        }}
        checked={value ? true : undefined}
      />
      <ConfirmationModal
        title={
          !value ? t("widgets.common.unpublish") : t("widgets.common.publish")
        }
        contentText={
          !value
            ? t("widgets.common.confirmUnpublish")
            : t("widgets.common.confirmPublish")
        }
        cancelButtonText={t("widgets.common.cancel")}
        cancelButtonAction={() => {
          setOpenedConfirmationModal(false);
          setValue(!value);
        }}
        confirmButtonText={
          !value ? t("widgets.common.unpublish") : t("widgets.common.publish")
        }
        confirmButtonAction={(e) => handleChange(e, id)}
        isOpen={openedConfirmationModal}
      />
    </>
  );
};

export const FileDocumentWidgetManager = ({ locals, goBack }) => {
  const { t } = useTranslation("coaching-portal");
  const activeWidget = locals.activeWidget;
  const widgetFiles = locals.widgetFiles;
  const activeUser = locals.activeUser;
  const organizationOwner = locals.organizationOwner;
  const userToRequest = getUserToRequest(activeUser, organizationOwner);
  const [formState, setFormState] = useState({}); //state to control the widget's form
  const [editState, setEditState] = useState({
    //state to edit a file
    name: "",
    file: "",
    description: "",
    widget: "",
    _id: "",
  });
  const [addNew, setAddNew] = useState("addNewButton"); //state to control if the "add new file" section is opened or not
  const [statusState, setStatusState] = useState("initial"); //state to control if the files are in "change order" mode or not
  const [widgetFileState, setWidgetFileState] = useState([]); //state to store the files of the widget
  const [editFileState, setEditFileState] = useState(""); //state to file the current file to can control the accordions
  //state to control the file's add new form
  const [fileFormState, setFileFormState] = useState({
    file: "",
    name: "",
    description: "",
    image: "",
  });
  const [imageState, setImageState] = useState(null);
  const [fileState, setFileState] = useState(null);
  const [refreshList, setRefreshList] = useState(false);
  const [editWidgetInfoModalOpened, setEditWidgetInfoModalOpened] =
    useState(false);
  const [changedOrder, setChangedOrder] = useState(false);

  /**
   * Reorder
   */
  useEffect(() => {
    let sortable = null;
    if (document.querySelector(".principal-grabber-div")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@1.15.7/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          sortable = new Sortable(
            document.querySelector(".principal-grabber-div"),
            {
              animation: 350,
              handle: ".drag-wrapper",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                const ordered = Array.from(items).map((element) => {
                  const fileInput = element.querySelector(".file-section-div");
                  const imageInput =
                    element.querySelector(".image-section-div");
                  const nameInput = element.querySelector(".name-section-div");
                  const descriptionInput = element.querySelector(
                    ".description-section-div"
                  );
                  const idInput = element.querySelector(".id-section-div");
                  const statusInput = element.querySelector(
                    ".status-section-div"
                  );
                  return {
                    file: (fileInput && JSON.parse(fileInput.value)) || "",
                    status: (statusInput && statusInput.value) || "",
                    description:
                      (descriptionInput && descriptionInput.textContent) || "",
                    name: (nameInput && nameInput.textContent) || "",
                    widget: activeWidget._id,
                    _id: (idInput && idInput.value) || "",
                    image: (imageInput && JSON.parse(imageInput.value)) || "",
                  };
                });
                setWidgetFileState(ordered);
                setChangedOrder(true);
                return false;
              },
            }
          );
        }
      );
    }
    return () => {
      if (sortable) sortable.destroy();
    };
  }, []);

  /**
   * Close the "add new file" section
   */
  useEffect(() => {
    if (locals.query.submited) {
      setFileFormState({ file: "", name: "", description: "", image: "" });
      setFileState(null);
      setImageState(null);
      setAddNew("addNewButton");
      setEditFileState("");
      setStatusState("initial");
    }
  }, [locals.query]);

  /**
   * Set the values to the states
   */
  const [compareBase, setCompareBase, setCurrentCompareState, hasChanged] =
    useCompareState();
  useEffect(() => {
    if (!!activeWidget) {
      const comparingBase = {
        name: activeWidget.name,
        description: activeWidget.description,
        icon: activeWidget.icon,
        pushPermission:
          activeWidget.pushPermission === undefined
            ? true
            : activeWidget.pushPermission,
        pullPermission:
          activeWidget.pullPermission === undefined
            ? true
            : activeWidget.pullPermission,
      };
      setFormState({
        ...comparingBase,
        id: activeWidget._id,
      });
      setCompareBase(comparingBase);
      setCurrentCompareState(comparingBase);
    }
    if (!!widgetFiles) {
      setWidgetFileState(widgetFiles);
    }
  }, [activeWidget, widgetFiles]);

  const {
    description = "",
    name = "",
    icon = "",
    id = 0,
    pushPermission = true,
    pullPermission = true,
  } = formState;

  /**
   * Validate if the widget's form is valid
   * @returns
   */
  const validationSubmit = () => {
    if (description.length > 0 && name.length > 0 && icon.length > 0) {
      return true;
    }
    return false;
  };

  /**
   * Delete a file
   * @param {*} e
   * @param {*} id
   */
  const handleDelete = (e, id) => {
    if (
      process.browser &&
      confirm(t("widgets.fileManager.confirmDeleteFile"))
    ) {
      e.stopPropagation();
      e.preventDefault();
      submit(`/actions/files/delete/${id}`, {
        method: "GET",
      });
    }
  };

  const handleInputChange = ({ target }) => {
    const targetName = target?.name.replace("-mobile", "") || target?.name;
    const newState = Object.assign({}, formState, {
      [targetName]: target.value,
    });
    setFormState(newState);
    setCurrentCompareState({
      ...compareBase,
      [targetName]: target.value,
    });
  };

  /**
   * Edit a file
   * @param {*} e
   */
  const handleEditSubmit = (e) => {
    e.preventDefault();
    setEditFileState("");
    setStatusState("initial");
    submit(`/actions/file-documents/save/${editState._id}`, {
      method: "POST",
      body: editState,
    });
  };

  /**
   * Add a file
   * @param {*} e
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    const body = formState;
    delete body.order;
    delete body.status;
    delete body.type;
    delete body.id;

    submit(`/actions/widgets/save/${id}`, {
      method: "POST",
      body: body,
    });
  };

  /**
   * Control if the "add new file" section is opened or not
   */
  const handleChangeAddNewState = () => {
    setAddNew("addNewForm");
  };

  /**
   * Control if the files are in "edit mode" or not
   * @param {*} state
   */
  const handleChangeEditState = (state) => {
    setStatusState(state);
  };

  /**
   * Delete a widget
   * @param {*} e
   */
  const deleteWidget = (e) => {
    if (!!activeWidget) {
      const { _id } = activeWidget;
      deleteWidgetUtil(e, _id);
    }
  };

  /**
   * Reorder
   * @param {*} e
   */
  const handleSaveReorder = (e) => {
    e.preventDefault();
    const orderedFileDocuments = widgetFileState.map((element) => element._id);

    if (orderedFileDocuments.length > 0) {
      setStatusState("initial");
      submit("/actions/files/reorder", {
        method: "POST",
        body: { orderedFileDocuments },
      });
    }
  };
  const cancelReorder = () => {
    setStatusState("initial");
    setWidgetFileState(widgetFiles);
    setRefreshList(!refreshList);
  };
  /**
   * Control if the files are in "edit mode" or not
   * @param {*} state
   */
  const handleChangeStatusState = (state) => {
    setStatusState(state);
  };

  /**
   * Validate if edit form is valid
   * @returns
   */
  const validationFileSubmit = () => {
    if (
      fileFormState.name.length > 0 &&
      fileFormState.description.length > 0 &&
      imageState !== null &&
      fileState !== null
    ) {
      return true;
    }
    return false;
  };

  /**
   * assign the value to the add new file state
   * @param {*} param0
   */
  const handleInputFileChange = ({ target }) => {
    const newState = Object.assign({}, fileFormState, {
      [target.name]: target.value,
    });
    setFileFormState(newState);
  };

  const handleImageUpload = (newImage) => {
    setImageState(newImage);
  };

  const handleFileUpload = (newFile) => {
    setFileState(newFile);
  };

  /**
   * Refresh the page cleaning the submited query
   */
  const handleRefresh = () => {
    submit(`/actions/refresh/`, {
      method: "GET",
      body: { submited: "" },
    });
  };

  /**
   * Open the edit widget info modal (only for mobile)
   */
  const onOpenEditWidgetInfo = () => {
    setEditWidgetInfoModalOpened(!editWidgetInfoModalOpened);
  };

  /**
   * Control the widget acess
   * @param {*} param0
   * @returns
   */
  const WidgetTogglePush = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.subscriptionsPush")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = Object.assign({}, formState, {
            pushPermission: !formState.pushPermission,
          });
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pushPermission: !formState.pushPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  const WidgetTogglePull = ({ permission }) => (
    <div className="labeled-switch" sz="large">
      <span>{t("widgets.common.coachingPull")}</span>
      <input
        type="checkbox"
        id="switch-1"
        name="switch-1"
        role="switch"
        onChange={() => {
          const newState = Object.assign({}, formState, {
            pullPermission: !formState.pullPermission,
          });
          setFormState(newState);
          setCurrentCompareState({
            ...compareBase,
            pullPermission: !formState.pullPermission,
          });
        }}
        checked={permission ? true : undefined}
      />
    </div>
  );

  return (
    <main className="widgets-manager list files-docs">
      <WidgetManagerHeader
        name={name}
        description={description}
        goBack={goBack}
        onOpenEditWidgetInfo={onOpenEditWidgetInfo}
      />
      <EditWidgetInfoModal
        widgetName={formState.name}
        widgetDescription={formState.description}
        widgetIcon={formState.icon}
        isOpen={editWidgetInfoModalOpened}
        onClose={onOpenEditWidgetInfo}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        validationSubmit={validationSubmit}
        iconsRadios={WIDGET_ICONS["file-documents"]}
        WidgetTogglePull={WidgetTogglePull}
        WidgetTogglePush={WidgetTogglePush}
        userToRequest={userToRequest}
        pullPermission={pullPermission}
        pushPermission={pushPermission}
      />
      <div>
        {!!activeWidget && (
          <aside className="mobile-hidden">
            <div className="overflow-auto max-h-[calc(100vh-16.75rem)]">
              <label>{t("widgets.common.widgetName")}</label>
              <input
                maxLength="30"
                name="name"
                className="text-input"
                placeholder={`Enter Name`}
                value={name}
                onChange={handleInputChange}
              />
              <label>{t("widgets.common.widgetDescription")}</label>
              <textarea
                maxLength="60"
                name="description"
                className="text-input"
                placeholder={t("widgets.common.enterDescriptionHere")}
                value={description}
                onChange={handleInputChange}
              />
              <ImageRadio
                label={t("widgets.common.widgetIcon")}
                name="icon"
                currentValue={icon}
                radios={WIDGET_ICONS["file-documents"]}
                onChange={handleInputChange}
                isManager={true}
              />
              <label>{t("widgets.common.userTypeAccess")}</label>
              {isPullActiveCoach(userToRequest) && (
                <WidgetTogglePull permission={pullPermission} />
              )}
              {userToRequest.pushAccountInfo && (
                <WidgetTogglePush permission={pushPermission} />
              )}
            </div>
            <footer>
              <button
                data-button-type="secondary"
                data-action="cancel"
                onClick={deleteWidget}
              >
                {t("widgets.common.delete")}
              </button>
              <button
                data-button-type="primary"
                onClick={handleSubmit}
                disabled={!hasChanged}
              >
                {t("widgets.common.save")}
              </button>
            </footer>
          </aside>
        )}
        <section>
          <header>
            <section className="only-show-mobile back-handler">
              <SvgIcon
                src={"/img/new-icons/back-button.svg"}
                onClickFn={() =>
                  submit(`/actions/refresh/?`, {
                    method: "GET",
                    body: { section: "", learn: "" },
                  })
                }
              />
              <h3>{activeWidget?.name || t("widgets.common.explore")}</h3>
            </section>
            <hgroup className="mobile-hidden">
              <h4>{t("widgets.fileManager.filesAndDocuments")}</h4>
              <p className="p6">
                {t("widgets.fileManager.sectionDescription")}
              </p>
            </hgroup>
            {statusState === "initial" ? (
              <button
                data-button-type="secondary"
                onClick={() => handleChangeStatusState("change-order")}
              >
                {t("widgets.common.changeOrder")}
              </button>
            ) : (
              <hgroup className="actions-wrapper">
                <button
                  data-button-type="primary"
                  onClick={
                    statusState === "change-order"
                      ? handleSaveReorder
                      : handleEditSubmit
                  }
                  disabled={!changedOrder}
                >
                  {t("widgets.common.save")}
                </button>
                {statusState === "change-order" && (
                  <button
                    data-button-type="secondary"
                    data-action="cancel"
                    onClick={cancelReorder}
                  >
                    {t("widgets.common.cancel")}
                  </button>
                )}
              </hgroup>
            )}
          </header>
          <div className="principal-grabber-div">
            {!!widgetFiles &&
              orderWidgets(widgetFileState).map(
                ({ _id, file, name, description, widget, status, image }) => {
                  return editFileState !== _id ||
                    statusState === "change-order" ? (
                    <div
                      key={`${_id}-${refreshList}`}
                      className="article-wrapper files-docs"
                    >
                      <article
                        id={_id}
                        className={`item-grabber-container${
                          statusState === "change-order" ? " column" : ""
                        }`}
                      >
                        <div className="column width-widget-information">
                          <div>
                            <input
                              className="file-section-div"
                              name="file"
                              style={{ display: "none" }}
                              readOnly
                              value={JSON.stringify(file)}
                            />
                            <input
                              className="image-section-div"
                              name="image"
                              style={{ display: "none" }}
                              readOnly
                              value={JSON.stringify(image)}
                            />
                            <input
                              className="id-section-div"
                              name="id"
                              style={{ display: "none" }}
                              readOnly
                              value={_id}
                            />
                            <input
                              className="status-section-div"
                              name="status"
                              style={{ display: "none" }}
                              readOnly
                              value={status}
                            />
                            <img
                              src={S3.toCDN(image?.s3Path || "")}
                              loading="lazy"
                            />
                          </div>
                          <div>
                            <p className="s2">{name}</p>
                            <p className="p4">{description}</p>
                          </div>
                        </div>
                        <div className="action-buttons">
                          <div>
                            <button data-button-type="secondary" data-icon-only>
                              <SvgIcon
                                src="/img/new-icons/edit-pencil.svg"
                                onClickFn={() => {
                                  setEditState({
                                    name: name,
                                    widget: widget,
                                    _id: _id,
                                    description: description,
                                    file: file,
                                  });
                                  handleChangeEditState("edit");
                                  setEditFileState(_id);
                                  handleRefresh();
                                }}
                              />
                            </button>
                            <button data-button-type="secondary" data-icon-only>
                              <SvgIcon
                                src={"/img/new-icons/red-trash.svg"}
                                onClickFn={(event) => handleDelete(event, _id)}
                                noFilterOnLight={true}
                              />
                            </button>
                          </div>
                          <div>
                            <WidgetToggle status={status} id={_id} />
                          </div>
                        </div>
                      </article>
                      {statusState === "change-order" && (
                        <div className="drag-wrapper">
                          <SvgIcon
                            src="/img/new-icons/menu.svg"
                            mobileSrc="/img/new-icons/drag.svg"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <EditFile
                      nameProps={name}
                      widgetProps={widget}
                      _idProps={_id}
                      descriptionProps={description}
                      fileProps={file}
                      imageProps={image}
                      widgetType={locals.query.type}
                    />
                  );
                }
              )}
          </div>
          {addNew === "addNewButton" ? (
            <div
              className="dashed-box"
              onClick={() => {
                handleChangeAddNewState();
                handleRefresh();
              }}
            >
              <SvgIcon src="/img/new-icons/add-square-outline.svg" alt="back" />
              <span>{t("widgets.fileManager.addNewFile")}</span>
            </div>
          ) : (
            <form
              encType="multipart/form-data"
              method="POST"
              data-noreset
              action={`/actions/file-documents/save/`}
              className="add-edit-file-wraper"
            >
              <div>
                <FileUpload
                  name="file"
                  prefix=""
                  handleFileUpload={handleFileUpload}
                />

                <ImageWidgetUploadInput
                  name="image"
                  prefix=""
                  defaultValue={fileFormState.image}
                  handleImageUpload={handleImageUpload}
                />
              </div>
              <div>
                <div>
                  <div className="input-box">
                    <label> File name </label>
                    <input
                      name="name"
                      className="input-videoplaylist-name text-input"
                      placeholder={`Enter file Name`}
                      value={fileFormState.name}
                      onChange={handleInputFileChange}
                    />
                  </div>
                  <div className="input-box">
                    <label> Description </label>
                    <input
                      name="description"
                      className="input-videoplaylist-name text-input"
                      value={fileFormState.description}
                      onChange={handleInputFileChange}
                      placeholder="Enter description"
                    />
                  </div>
                </div>
                <input
                  name="widget"
                  style={{ display: "none" }}
                  defaultValue={id}
                />
                <button
                  disabled={!validationFileSubmit()}
                  data-button-type="primary"
                >
                  {t("widgets.common.save")}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
};
